import { useState, useEffect } from "react";
import requestHandler from "../utils/requestHandler.js";
import { BorderBox, Timeline, Spinner } from "../components/index.js";
import ManagedVimeoPlayer from "../components/ManagedVimeoPlayer.jsx";
import { useParams } from "react-router-dom";

const helpfulLinks = [
  {
    title: "Imperator Messages",
    link: "https://amorc.customerhub.net/messages-from-the-imperator",
  },
  {
    title: "Grand Master Messages",
    link: "https://amorc.customerhub.net/grand-master-messages",
  },
  {
    title: "New Member Handbook (pdf)",
    link: "https://kutumbua.net/storage/uploads/New%20Member%20Handbook%2017June2026.pdf",
  },
  {
    title: "Access the Rosicrucian Forum magazine",
    link: "https://amorc.customerhub.net/rosicrucian-forum",
  },
  {
    title: "Conveniently hear PDF files read aloud",
    link: "https://www.adobe.com/acrobat/hub/how-to-read-pdf-aloud.html",
  },
];

const appLinks = [
  {
    title: "Rosicrucian Vowel Sounds App",
    link: "https://rosicrucian.org/vowel-sound-app",
  },
  {
    title: "AMORC Resources App",
    link: "https://www.rosicrucian.org/resources-app",
  },
  {
    title: "Rosicrucian Parenting App",
    link: "https://rosicrucian-parenting.org/",
  },
];

const getDegreeVideo = (degreeSlug) => {
  switch (degreeSlug) {
    case "mandami":
      return "https://player.vimeo.com/video/1168859961?h=4bd7bbe608";
    case "atrium-one":
      return "https://player.vimeo.com/video/1167407076?h=a12a9d4da5";
    case "atrium-two":
      return "https://player.vimeo.com/video/1167752513?h=0fd80131bd";
    case "atrium-three":
      return "https://player.vimeo.com/video/1167752532?h=886b35a9c2";
    default:
      return "https://player.vimeo.com/video/1168859961?h=4bd7bbe608";
  }
};

const getDegreeTranscript = (degreeSlug) => {
  switch (degreeSlug) {
    case "mandami":
      return "https://rosc-public.nyc3.cdn.digitaloceanspaces.com/pdfs/Mandamus_Welcome_Video_Transcript_021026.pdf";
    case "atrium-one":
      return "https://kutumbua.net/storage/uploads/0Atrium_1_Degree_Welcome%20Transcript%20022326.pdf";
    case "atrium-two":
      return "https://rosc-public.nyc3.cdn.digitaloceanspaces.com/pdfs/Atrium_2_Welcome_Transcript_121625.pdf";
    case "atrium-three":
      return "https://rosc-public.nyc3.cdn.digitaloceanspaces.com/pdfs/Atrium_3_Welcome Transcript_121325.pdf";
    default:
      return "https://rosc-public.nyc3.cdn.digitaloceanspaces.com/pdfs/Mandamus_Welcome_Video_Transcript_021026.pdf";
  }
};

export default function Degree() {
  const [degree, setDegree] = useState([]);
  const [loading, setLoading] = useState(true);
  const { degreeSlug } = useParams();

  useEffect(() => {
    const loadDegree = async () => {
      setLoading(true);
      const res = await requestHandler({
        path: "degrees",
        method: "POST",
        body: { slug: degreeSlug },
      });

      if (res.success) {
        setDegree(res.data[0]);
      } else {
        console.error("Error loading degrees:", res.error);
      }
      setLoading(false);
    };

    loadDegree();
  }, [degreeSlug]);

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-2 lg:grid-rows-2 gap-4 pb-4 md:h-[calc(100vh-4rem)]">
      {/* Video Section - First on mobile, Col 2 Row 1 on Desktop */}
      <div className="w-full h-[15%] md:h-full lg:col-start-2 lg:row-start-1">
        <BorderBox noPadding className="overflow-hidden">
          <div className="flex flex-col items-center h-full w-full">
            <ManagedVimeoPlayer
              videoUrl={getDegreeVideo(degreeSlug)}
              title="Degree Welcome Video"
              width="100%"
              className="flex-1 min-h-0"
              style={{ aspectRatio: "16/9" }}
            />
            <a
              href={getDegreeTranscript(degreeSlug)}
              target="_blank"
              className="text-lz-ember dark:text-lz-candle self-start hover:underline pl-6 pb-2 mt-2 shrink-0"
            >
              Download Video Transcript
            </a>
          </div>
        </BorderBox>
      </div>

      {/* Timeline Section - Second on mobile, Col 1 (spans 2 rows) on Desktop */}
      <div className="w-full h-auto lg:h-full lg:col-start-1 lg:row-start-1 lg:row-span-2">
        <BorderBox overflow padding="p-0">
          <div className="h-full">
            {loading ? <Spinner /> : <Timeline degree={degree} />}
          </div>
        </BorderBox>
      </div>

      {/* Links Section - Last on mobile, Col 2 Row 2 on Desktop */}
      <div className="w-full h-auto lg:h-full lg:col-start-2 lg:row-start-2">
        <BorderBox padding="p-6" overflow>
          <div className="flex flex-col gap-4 h-full w-full text-lz-ember dark:text-lz-candle">
            <h2 className="uppercase text-lz-ink-mid dark:text-lz-concrete">
              Helpful Links
            </h2>

            <ul className="flex flex-col gap-2">
              {helpfulLinks.map((link, index) => (
                <li key={index} className="hover:underline">
                  <a href={link.link} target="_blank">
                    {link.title}
                  </a>
                </li>
              ))}
            </ul>

            <h2 className="uppercase text-lz-ink-mid dark:text-lz-concrete mt-4">
              Rosicrucian Apps
            </h2>

            <ul className="flex flex-col gap-2">
              {appLinks.map((link, index) => (
                <li key={index} className="hover:underline">
                  <a href={link.link} target="_blank">
                    {link.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </BorderBox>
      </div>
    </div>
  );
}
